// @flow

import { all, put, takeEvery, call } from "redux-saga/effects";
import type { Action } from "redux-action";

import http from "@http";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import actionCreators from "@modules/service/actionCreator";
import type { SpecialContactsType } from "@modules/service/reducer/special-contacts-types/types";
import {
  GetSpecialContactTypes,
  AddSpecialContactType,
  UpdateSpecialContactType,
  RemoveSpecialContactType,
} from "@modules/service/constants/actions";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type SpecialContactTypesListRequest = $Exact<{
  clientId: string,
}>;

type SpecialContactsRemoveRequest = $Exact<{
  ...SpecialContactTypesListRequest,
  specialContactTypeId: number,
}>;

export function* onGetSpecialContactTypes({
  payload,
}: Action<SpecialContactTypesListRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<SpecialContactsType>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/special-contact-type`,
    );

    yield put(actionCreators.GetSpecialContactTypesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetSpecialContactTypesError(e.response));
  }
}

export function* onAddSpecialContactType({
  payload,
}: Action<*>): Generator<*, void, SpecialContactsType> {
  try {
    if (!payload || !payload.clientId || !payload.data) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/special-contact-type`,
      payload.data,
    );

    yield all([
      put(actionCreators.AddSpecialContactTypeSuccess()),
      put(actionCreators.SetShowSpecialContactTypeModal(false)),
      put(sendSuccessMessage("CUSTOM_CONTACT_CREATED")),
      put(
        actionCreators.GetSpecialContactTypes({
          clientId: payload.clientId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.AddSpecialContactTypeError(e.response));
  }
}

export function* onUpdateSpecialContactType({
  payload,
}: Action<*>): Generator<*, void, SpecialContactsType> {
  try {
    if (!payload || !payload.clientId || !payload.specialContactTypeId || !payload.data) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/special-contact-type/${payload.specialContactTypeId}`,
      payload.data,
    );

    yield all([
      put(actionCreators.UpdateSpecialContactTypeSuccess()),
      put(actionCreators.SetShowSpecialContactTypeModal(false)),
      put(sendSuccessMessage("CUSTOM_CONTACT_UPDATED")),
      put(
        actionCreators.GetSpecialContactTypes({
          clientId: payload.clientId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateSpecialContactTypeError(e.response));
  }
}

export function* onRemoveSpecialContactType({
  payload,
}: Action<SpecialContactsRemoveRequest>): Generator<*, void, SpecialContactsType> {
  try {
    if (!payload || !payload.clientId || !payload.specialContactTypeId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/special-contact-type/${payload.specialContactTypeId}`,
    );

    yield all([
      put(actionCreators.RemoveSpecialContactTypeSuccess()),
      put(actionCreators.SetShowDeleteSpecialContactTypeModal(false)),
      put(sendSuccessMessage("CUSTOM_CONTACT_REMOVED")),
      put(
        actionCreators.GetSpecialContactTypes({
          clientId: payload.clientId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.RemoveSpecialContactTypeError(e.response));
  }
}

export function* SpecialContactTypesSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetSpecialContactTypes, onGetSpecialContactTypes),
    takeEvery(AddSpecialContactType, onAddSpecialContactType),
    takeEvery(UpdateSpecialContactType, onUpdateSpecialContactType),
    takeEvery(RemoveSpecialContactType, onRemoveSpecialContactType),
  ]);
}

export default SpecialContactTypesSagas;
